import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import {
  Shield, Archive, BookOpen, Heart, ChevronRight, User,
  Image, Video, Mic, FileText, Lock, ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const typeIcons = { photo: Image, video: Video, voice_note: Mic, document: FileText };

export default function AdminPage() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [activeTab, setActiveTab] = useState("vault");
  const [vaultEntries, setVaultEntries] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [legacyMessages, setLegacyMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const res = await api.get("/admin/accounts");
        setAccounts(res.data.accounts);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    loadAccounts();
  }, []);

  const loadAccountData = async (uid) => {
    try {
      const [v, j, l] = await Promise.all([
        api.get(`/admin/vault/${uid}`),
        api.get(`/admin/journal/${uid}`),
        api.get(`/admin/legacy/${uid}`),
      ]);
      setVaultEntries(v.data.entries);
      setJournalEntries(j.data.entries);
      setLegacyMessages(l.data.messages);
    } catch {
      toast.error("Failed to load account data");
    }
  };

  const handleSelectAccount = (acc) => {
    setSelectedAccount(acc);
    loadAccountData(acc.user_id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-12">
        <div className="w-8 h-8 border-2 border-[#00FF88] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="p-6 md:p-12 max-w-4xl mx-auto" data-testid="admin-page-empty">
        <div className="mb-8">
          <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-2 font-medium">Admin Panel</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Managed Accounts</h1>
        </div>
        <div className="text-center py-24">
          <Shield className="w-12 h-12 text-slate-700 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">No accounts to manage</p>
          <p className="text-slate-600 text-sm mt-2">When you claim a beneficiary account, it will appear here.</p>
        </div>
      </div>
    );
  }

  if (!selectedAccount) {
    return (
      <div className="p-6 md:p-12 max-w-4xl mx-auto" data-testid="admin-page">
        <div className="mb-8">
          <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-2 font-medium">Admin Panel</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Managed Accounts</h1>
          <p className="text-sm text-slate-400 mt-1">Access vaults you've been trusted to manage</p>
        </div>
        <div className="space-y-4">
          {accounts.map((acc, i) => (
            <motion.div
              key={acc.user_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => handleSelectAccount(acc)}
              className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 cursor-pointer hover:border-[#00FF88]/20 transition-colors duration-300 group"
              data-testid={`admin-account-${acc.user_id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-[#00FF88]/10 flex items-center justify-center">
                    <User className="w-6 h-6 text-[#00FF88]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Manrope' }}>{acc.name}</h3>
                    <p className="text-sm text-slate-400">{acc.email}</p>
                    <div className="flex gap-4 mt-2">
                      <span className="text-xs text-slate-500">{acc.stats?.vault || 0} vault items</span>
                      <span className="text-xs text-slate-500">{acc.stats?.journal || 0} journal entries</span>
                      <span className="text-xs text-slate-500">{acc.stats?.legacy || 0} legacy messages</span>
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-[#00FF88] transition-colors" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  // Account detail view
  const tabs = [
    { key: "vault", label: "Vault", icon: Archive, count: vaultEntries.length },
    { key: "journal", label: "Journal", icon: BookOpen, count: journalEntries.length },
    { key: "legacy", label: "Legacy Messages", icon: Heart, count: legacyMessages.length },
  ];

  return (
    <div className="p-6 md:p-12 max-w-6xl mx-auto" data-testid="admin-account-detail">
      {/* Back + Header */}
      <button
        onClick={() => setSelectedAccount(null)}
        className="text-sm text-slate-400 hover:text-white mb-6 flex items-center gap-1 transition-colors"
        data-testid="admin-back-btn"
      >
        <ChevronRight className="w-4 h-4 rotate-180" /> Back to accounts
      </button>
      <div className="mb-8">
        <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-2 font-medium">Admin View</p>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          {selectedAccount.name}'s Vault
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-8 glass-card rounded-xl p-1.5 w-fit">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
              activeTab === t.key ? "bg-white/10 text-white" : "text-slate-500 hover:text-white"
            }`}
          >
            <t.icon className="w-4 h-4" strokeWidth={1.5} />
            {t.label}
            <span className="text-xs bg-white/5 px-1.5 py-0.5 rounded">{t.count}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === "vault" && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {vaultEntries.map((entry) => {
            const Icon = typeIcons[entry.entry_type] || FileText;
            return (
              <div key={entry.entry_id} className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl overflow-hidden">
                {entry.content_type?.startsWith("image") ? (
                  <img src={`${BACKEND_URL}/api/vault/files/${entry.file_id}`} alt={entry.title} className="w-full h-40 object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-40 bg-white/5 flex items-center justify-center">
                    <Icon className="w-10 h-10 text-slate-700" strokeWidth={1.5} />
                  </div>
                )}
                <div className="p-4">
                  <p className="text-sm font-medium text-white truncate">{entry.title}</p>
                  <p className="text-xs text-slate-500 mt-1">{new Date(entry.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            );
          })}
          {vaultEntries.length === 0 && <p className="text-slate-500 text-sm col-span-full py-12 text-center">No vault entries</p>}
        </div>
      )}

      {activeTab === "journal" && (
        <div className="space-y-4">
          {journalEntries.map((entry) => (
            <div key={entry.entry_id} className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
              <h3 className="text-lg font-medium text-white" style={{ fontFamily: 'Manrope' }}>{entry.title}</h3>
              {entry.mood && <span className="text-xs bg-white/5 text-slate-400 px-2 py-0.5 rounded-full mt-1 inline-block">{entry.mood}</span>}
              <div className="text-sm text-slate-300 mt-3 leading-relaxed" dangerouslySetInnerHTML={{ __html: entry.content }} />
              <p className="text-xs text-slate-600 mt-3">{new Date(entry.created_at).toLocaleDateString()}</p>
            </div>
          ))}
          {journalEntries.length === 0 && <p className="text-slate-500 text-sm py-12 text-center">No journal entries</p>}
        </div>
      )}

      {activeTab === "legacy" && (
        <div className="space-y-4">
          {legacyMessages.map((msg) => (
            <div key={msg.message_id} className="bg-white/[0.03] backdrop-blur-xl border border-[#00FF88]/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Lock className="w-4 h-4 text-[#00FF88]" />
                <h3 className="text-lg font-medium text-white" style={{ fontFamily: 'Manrope' }}>{msg.title}</h3>
                {msg.is_released && <span className="text-xs bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-full">Released</span>}
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">{msg.content}</p>
              {msg.recipients?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {msg.recipients.map((r, ri) => (
                    <span key={ri} className="text-xs bg-white/5 px-3 py-1 rounded-full text-slate-400">{r.name || r.email}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
          {legacyMessages.length === 0 && <p className="text-slate-500 text-sm py-12 text-center">No legacy messages</p>}
        </div>
      )}
    </div>
  );
}
